/**
 * The chat transport, kept out of the component so it can be tested without a browser or the
 * app's runtime env. What matters here is what reaches Jacky's eyes: a frame we cannot parse is
 * dropped rather than rendered as protocol, a half-arrived frame waits for the rest, and the page
 * he is on travels with the question so "this job" means the job on screen.
 */

/** One SSE frame from EmmA's turn. `data: {...}\n\n` — an empty or unparsable frame is skipped,
 *  never shown: a chat that renders its own protocol is worse than one that lags a beat.
 *
 *  `error` IS A FRAME, NOT AN ABSENCE. Her turn loop sends
 *  `{type:'error', message}` when the model or a tool fails, and this type did not
 *  carry it — so the one frame that says "I could not do this" was the one frame the
 *  drawer had no branch for. See the note in the drawer. */
import {readTurnFiles, type TurnFile} from './emma-attachments';

export type ChatEvent =
  | {type: 'reasoning'; delta?: string; [k: string]: unknown}
  /** One step of her process. `label` is the line a person reads ("Looking up the job"); `name` is
   *  the tool's code name, kept for the "Show details" fold. */
  | {type: 'tool'; name?: string; phase?: 'start' | 'ok' | 'error'; detail?: string | null; label?: string; [k: string]: unknown}
  | {type: 'status'; state?: 'thinking' | 'running' | 'replying' | 'polishing'; message?: string; label?: string; [k: string]: unknown}
  /** Her final words as they are written. `reset` clears what was shown: the draft broke the plain
   *  shape and is being rewritten, or those words sat beside a tool call and were never the reply. */
  | {type: 'reply'; delta?: string; reset?: true}
  /** The session this turn belongs to. THE SERVER NOW SENDS IT FIRST — before the model is called —
   *  so a slow, failed or cut-off turn still tells the client which conversation it was in. It used
   *  to arrive after `done`, which meant a 400 from the provider lost the id and the next ask started
   *  a thread nobody could read back. */
  | {type: 'session'; sessionId: string; resumed?: boolean}
  /** She opened a NEW conversation mid-turn because the subject moved (`branch_chat`). The current
   *  answer stays where it is; the client adopts this id for the NEXT ask. */
  | {type: 'branched'; sessionId: string; from?: string | null}
  | {type: 'error'; message?: string}
  | {type: 'done'; result: {reply?: string; steps?: unknown[]; proposals?: {id?: string; title?: string}[]; navigate?: string | null; model?: string; iterations?: number; chatSessionId?: string | null; branchedTo?: string | null}};

/** One line of her process, as a person reads it. */
export type ProgressLine = {label: string; state: 'doing' | 'ok' | 'error'; tool?: string; detail?: string | null};

/**
 * Fold one `tool` or `status` event into the lines a person sees. A round ("Thinking it through",
 * "Checking it saved", "Writing back to you") settles when the next line arrives; a tool line
 * settles when its own ok/error arrives. The code name is kept on the line for the details fold.
 */
export function applyProgress(lines: ProgressLine[], e: ChatEvent): ProgressLine[] {
  const settle = (ls: ProgressLine[]) => ls.map((l) => (l.state === 'doing' && !l.tool ? {...l, state: 'ok' as const} : l));
  if (e.type === 'tool') {
    const name = String(e.name ?? e.tool ?? '');
    const label = e.label ?? name.replace(/_/g, ' ');
    if (!label) return lines;
    if (e.phase === 'start') return [...settle(lines), {label, state: 'doing', tool: name}];
    let i = lines.length - 1;
    while (i >= 0 && !(lines[i]?.tool === name && lines[i]?.state === 'doing')) i--;
    const next: ProgressLine = {label, state: e.phase === 'error' ? 'error' : 'ok', tool: name, detail: e.detail ?? null};
    return i >= 0 ? lines.map((l, j) => (j === i ? next : l)) : [...lines, next];
  }
  if (e.type === 'status' && e.state !== 'running' && e.label) {
    const last = lines[lines.length - 1];
    if (last && last.label === e.label && !last.tool) return lines;
    return [...settle(lines), {label: e.label, state: 'doing'}];
  }
  return lines;
}

export function parseChatFrame(frame: string): ChatEvent | null {
  const line = frame.split('\n').find((l) => l.startsWith('data:'));
  if (!line) return null;
  const raw = line.slice(5).trim();
  if (!raw || raw === '[DONE]') return null;
  try {
    const parsed = JSON.parse(raw) as ChatEvent;
    return parsed && typeof parsed === 'object' && 'type' in parsed ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Split a growing SSE buffer into whole frames, returning what is complete and what is left.
 *
 * CRLF IS VALID SSE and a proxy is allowed to hand it back — the server writes `\n\n`, and
 * nginx or an ingress in between may re-frame it as `\r\n\r\n`. Splitting on `'\n\n'` alone
 * does not match that, so every frame stayed in the buffer, nothing was ever parsed, and the
 * drawer sat on "working…" for as long as the connection lived. Splitting on the blank line
 * itself (`\r?\n\r?\n`) reads either, and is what the SSE spec's frame boundary means.
 */
export function splitFrames(buffer: string): {frames: string[]; rest: string} {
  const parts = buffer.split(/\r?\n\r?\n/);
  return {frames: parts.slice(0, -1), rest: parts[parts.length - 1] ?? ''};
}

/** Where he is, so she answers about the thing on his screen. */
export function pageContext(pathname: string): {kind: string; ref: string | null} {
  const [, first = '', second = ''] = pathname.split('/');
  const ref = second && /^[A-Za-z0-9-]{3,}$/.test(second) ? second : null;
  return {kind: first || 'home', ref};
}


/** The session id her turns belong to, remembered per staff member.
 *
 *  Without this the drawer starts a fresh conversation every time it mounts, so leaving the page and
 *  coming back showed an empty thread — Jacky: "opening full desk need to show historical chat and
 *  her reply, currently only show 1 and navigate back clears to 0". The session is the key her
 *  history is stored under, so it has to outlive the component. */
const SESSION_KEY = 'aiw.emma.session';

export function getEmmaSession(scope = 'default'): string | null {
  try { return localStorage.getItem(`${SESSION_KEY}.${scope}`); } catch { return null; }
}

export function setEmmaSession(id: string | null, scope = 'default'): void {
  try { id ? localStorage.setItem(`${SESSION_KEY}.${scope}`, id) : localStorage.removeItem(`${SESSION_KEY}.${scope}`); } catch { /* private mode */ }
}

/** One past turn, as her store keeps it: the message, her reply, and when. */
export type HistoryTurn = {
  message: string;
  reply: string;
  model: string | null;
  created_at: string;
  /** WHICH SHAPE THE ROW CAME IN — see `readTurns`.
   *
   *  `/api/agent/history` has ALWAYS answered with the replay shape: one row per message,
   *  `{role, text}` (`turnsToMessages` → `ReplayMsg`, emma-hub). This type said `{message, reply}`
   *  and the read asserted it with `as HistoryTurn[]`, so the mismatch was invisible for weeks —
   *  every row really was `{role, text}` and nothing looked. When a reader was finally written that
   *  DID check (18 September), it silently dropped all of them, and Jacky's conversation read as
   *  *"her store returned 0 turns for it"* with three turns sitting in the table.
   *
   *  Both shapes are therefore optional fields, normalized in one place and assumed nowhere. */
  role?: 'user' | 'emma';
  text?: string;
  /** ER-08: the files handed in WITH this message (the replay shape puts them on the user row). Absent
   *  when there were none, so a turn without files has no empty list to render. */
  attachments?: TurnFile[];
};

/** One conversation, as the left pane lists it. */
export type Conversation = {
  id: string; title: string; surface: string | null; anchorRef: string | null;
  turnCount: number; lastTurnAt: string | null; createdAt: string;
  /** Set when this conversation was branched off another — the lineage the pane shows. */
  inheritsFrom: string | null; branchReason: string | null;
};

/**
 * Read the conversation back from HER store — never from component state.
 *
 * THE AUTH HEADER IS THE FIX, and its absence was the whole symptom. `/emma-history/` is proxied to
 * `/api/agent/history`, which sits behind `requireAuth` on EmmA's API; this call sent only `accept`,
 * so it answered **401 Unauthorized: missing bearer token** — and the caller's `.catch{}` swallowed
 * it. Jacky saw an empty thread and said the only thing that was true from where he sat:
 * *"talk to emma historical chat doesnt exist"*, and then, tonight, *"last 2 chat is actually 1
 * conversation, but system treating as 2"*. Every hand-rolled fetch in this app passes `authHeaders`
 * (customer-correspondence, lead-alerts, the whiteboard); this one was the exception.
 *
 * The headers come from the CALLER rather than being imported here, so this module stays free of the
 * app's Supabase client and can be tested in plain Node (`node --test`).
 */
/**
 * A failed read carries its REASON, not just its failure.
 *
 * `new Error('History unavailable')` is what made tonight's bug invisible twice over: the drawer
 * catches a history error so a bad read can never break the chat, and a bare message means the catch
 * has nothing to say. Jacky's screenshot settled it — six turns showing `working…` with empty reply
 * bubbles, which is exactly what a thread looks like when the read fails and the fallback is silence.
 * With the status on the error, the drawer can say "401 — sign in again" instead of showing nothing.
 */
export class EmmaReadError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'EmmaReadError';
    this.status = status;
  }
}

export async function loadEmmaHistory(
  sessionId: string,
  headers: Record<string, string>,
  limit = 50,
  /** Called once on a 401 to mint a live bearer. Omitted in tests, which drive the failure directly. */
  refreshAuth?: () => Promise<Record<string, string> | null>,
): Promise<HistoryTurn[]> {
  const body = await authedJson(`/emma-history/?sessionId=${encodeURIComponent(sessionId)}&limit=${limit}`, headers, 'History unavailable', refreshAuth);
  return readTurns((body as {messages?: unknown}).messages);
}

/**
 * READ WITH A LIVE BEARER, ONCE, RETRYING ONE 401.
 *
 * This is the fix for the last rung of tonight's ladder: the read answered **200 with four messages**
 * when driven against production with a fresh token, while the drawer came up empty in Jacky's
 * browser — *"now no error but historical conversation all empty, this is correct?"*. Nothing was
 * lost, and nothing was wrong with the endpoint: his tab was holding an access token past its hour.
 *
 * Every call site would otherwise need to know about expiry, and none of them should. On a 401 the
 * session is refreshed once (`refreshAuth` — the app's Supabase client, injected so this module stays
 * testable in plain Node) and the request is retried with the new token. A second 401 is a real
 * answer, not a retry: it means the sign-in itself is gone, and it is reported as such.
 */
export async function authedJson(
  url: string,
  headers: Record<string, string>,
  what: string,
  refreshAuth?: () => Promise<Record<string, string> | null>,
): Promise<Record<string, unknown>> {
  const once = async (h: Record<string, string>) => {
    const r = await fetch(url, {headers: {...h, accept: 'application/json'}});
    const body = (await r.json().catch(() => null)) as Record<string, unknown> | null;
    return {status: r.status, ok: r.ok, body};
  };
  let res = await once(headers);
  if (res.status === 401 && refreshAuth) {
    const fresh = await refreshAuth().catch(() => null);
    if (fresh) res = await once({...headers, ...fresh});
  }
  if (!res.ok || !res.body?.ok) {
    const reason = (res.body?.warning ?? res.body?.error ?? `${what} (${res.status})`) as string;
    throw new EmmaReadError(String(reason), res.status);
  }
  return res.body;
}

/**
 * WHAT A TURN IS, ENFORCED RATHER THAN ASSUMED.
 *
 * This is where the drawer died. Jacky's screen: *"Her drawer failed to render … Cannot read
 * properties of undefined (reading 'split')"* — the one and only `.split` in that component, in the
 * title fallback, running over a turn whose `message` was not a string. A row that is shaped wrong
 * must never travel as though it were a turn: whatever the server (or a schema change, or a legacy
 * row) hands over, what leaves this function is `HistoryTurn[]` in fact and not only in TypeScript.
 *
 * A turn with no QUESTION is dropped — it cannot be rendered, and a title cannot be made from it. A
 * turn with no REPLY is kept, with an empty answer, because the question is his and losing it would
 * be losing his own words.
 */
export function readTurns(raw: unknown): HistoryTurn[] {
  if (!Array.isArray(raw)) return [];
  const out: HistoryTurn[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const t = row as Record<string, unknown>;

    // THE REPLAY SHAPE, which is what `/api/agent/history` has always sent: one row per message.
    // Consecutive `user` then `emma` are ONE turn, and this pairing is what puts the thread back on
    // screen — the reader that knew only `{message, reply}` dropped every row and reported an empty
    // conversation while three turns sat in the table.
    const role = t.role === 'user' ? 'user' : t.role === 'emma' || t.role === 'assistant' ? 'emma' : null;
    if (role) {
      const text = typeof t.text === 'string' ? t.text : typeof t.content === 'string' ? t.content : '';
      if (!text.trim()) continue;
      const model = typeof t.model === 'string' ? t.model : null;
      const at = typeof t.created_at === 'string' ? t.created_at : '';
      if (role === 'emma') {
        const last = out[out.length - 1];
        // Her answer closes the question in front of it.
        if (last && last.role === 'user' && !last.reply) { last.reply = text; continue; }
        // An answer whose question was cut off by the limit still shows HER words.
        out.push({message: '', reply: text, model, created_at: at, role: 'emma', text});
        continue;
      }
      const files = readTurnFiles(t.attachments);
      out.push({message: text, reply: '', model, created_at: at, role: 'user', text, ...(files.length ? {attachments: files} : {})});
      continue;
    }

    // The turn shape: `{message, reply}`.
    const message = typeof t.message === 'string' ? t.message : '';
    if (!message.trim()) continue;
    out.push({
      message,
      reply: typeof t.reply === 'string' ? t.reply : '',
      model: typeof t.model === 'string' ? t.model : null,
      created_at: typeof t.created_at === 'string' ? t.created_at : '',
    });
  }
  // A turn with neither side is nothing, and a lone answer with no question is not a turn the thread
  // can render — dropped HERE, so the renderer never has to defend itself.
  return out.filter((t) => (t.message.trim() || t.reply.trim()) && !(t.role === 'emma' && !t.message.trim()));
}

/** The operator's conversations, newest first — the list the drawer's left pane is built on.
 *  Fails soft: a list that cannot load must not stop him talking to her. */
export async function loadConversations(
  headers: Record<string, string>,
  limit = 30,
  refreshAuth?: () => Promise<Record<string, string> | null>,
): Promise<Conversation[]> {
  const body = await authedJson(`/emma-conversations/?limit=${limit}`, headers, 'Conversations unavailable', refreshAuth);
  return readConversations((body as {sessions?: unknown}).sessions);
}

/** Same rule as `readTurns`: a row that cannot be rendered is not a conversation. One without an id
 *  or a title is dropped rather than rendered blank — the ledger has to be able to name it. */
export function readConversations(raw: unknown): Conversation[] {
  if (!Array.isArray(raw)) return [];
  const out: Conversation[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const c = row as Record<string, unknown>;
    const id = typeof c.id === 'string' ? c.id : '';
    if (!id) continue;
    const count = Number(c.turnCount);
    out.push({
      id,
      title: (typeof c.title === 'string' && c.title.trim()) || '(untitled conversation)',
      surface: typeof c.surface === 'string' ? c.surface : null,
      anchorRef: typeof c.anchorRef === 'string' ? c.anchorRef : null,
      turnCount: Number.isFinite(count) ? count : 0,
      lastTurnAt: typeof c.lastTurnAt === 'string' ? c.lastTurnAt : null,
      createdAt: typeof c.createdAt === 'string' ? c.createdAt : '',
      inheritsFrom: typeof c.inheritsFrom === 'string' ? c.inheritsFrom : null,
      branchReason: typeof c.branchReason === 'string' ? c.branchReason : null,
    });
  }
  return out;
}

/** Jacky, 5 October 2026: "Emma should talk to me … provide me fact, suggest in human readable suggestion". When she offers a
 * real choice she ends her reply with `[[A | B | C]]` on its own last line (emma-hub persona, "How you talk to Jacky");
 * the drawer turns it into buttons. Two to four short replies; anything else is left as plain text. */
export function quickReplies(text: string): {text: string; replies: string[]} {
  const lines = text.replace(/\s+$/, '').split('\n');
  const last = lines[lines.length - 1]?.trim() ?? '';
  const m = /^\[\[(.+)\]\]$/.exec(last);
  if (!m) return {text, replies: []};
  const replies = m[1]!.split('|').map((r) => r.trim()).filter((r) => r.length > 0 && r.length <= 60);
  if (replies.length < 2 || replies.length > 4) return {text, replies: []};
  return {text: lines.slice(0, -1).join('\n').replace(/\s+$/, ''), replies};
}

